import { FormattedMessage, useIntl } from 'react-intl'

import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { ArrowUpIcon } from '@gto/ui/icons/outline'
import { StarIcon, TrophyIcon } from '@gto/ui/icons/solid'
import { Typography } from '@gto/ui/typography'

import type { RakeRaceLeaderboard } from '@/api/gen'
import { fluidColumn } from '@/components/table-columns'
import { USER_RANK_ROW } from '@/pages/promotion/components/rake-race/constants'
import { safeNumber } from '@/utils/helpers'

import { useRacePrizes } from './use-race-prizes'

export type RakeRaceRow = RakeRaceLeaderboard & {
  id: number | string
  prize: string
  prizeValue: number
}

const isUserRankRow = (row: RakeRaceRow) => row.id === USER_RANK_ROW

export const useRakeRaceTableCols = (leaderboard: RakeRaceRow[] = []): ColumnDef<RakeRaceRow>[] => {
  const { formatMessage, formatNumber } = useIntl()
  const prizes = useRacePrizes()

  const usd = (value: number, minimumFractionDigits?: number) =>
    formatNumber(value, { style: 'currency', currency: 'USD', minimumFractionDigits })

  /** The prize ladder entry for a rank — both prize columns read the same lookup. */
  const prizeForRank = (rank: number) => prizes.find((prize) => prize.id === rank)

  return createColumns<RakeRaceRow>([
    {
      accessorKey: 'rank',
      header: formatMessage({ id: 'promo.rakeRace.table.place' }),
      ...fluidColumn({ min: 220, align: 'center', headerAlign: 'center' }),
      cell: ({ row }) => {
        const { rank } = row.original

        if (!rank) {
          return '-'
        }

        const isUserRow = isUserRankRow(row.original)

        return (
          <div className="flex flex-col gap-2">
            <Typography
              color={isUserRow ? 'primary' : 'text.primary'}
              variant="body2"
            >
              {rank}
            </Typography>
            {isUserRow && (
              <Typography
                color="primary"
                variant="body2"
              >
                <FormattedMessage id="promo.rakeRace.table.you" />
              </Typography>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'score',
      header: formatMessage({ id: 'promo.rakeRace.table.rakeGgr' }),
      ...fluidColumn({ min: 120 }),
      cell: ({ row }) => {
        const { rank, score } = row.original
        const value = safeNumber(score)

        if (!rank) {
          return '-'
        }

        const readout = value ? usd(value) : '-'

        if (!isUserRankRow(row.original)) {
          return readout
        }

        // The user's own row carries the gap to the place above it, so it reads as progress
        // rather than a bare number.
        const nextPlace = rank - 1 === 0 ? null : rank - 1
        const nextPlaceRake =
          nextPlace === null
            ? 0
            : safeNumber(leaderboard.find((entry) => entry.rank === nextPlace)?.score ?? 0)

        return (
          <div className="flex items-center gap-2">
            <div className="text-primary-main">
              {rank === 1 && (
                <TrophyIcon
                  height={24}
                  width={24}
                />
              )}
              {[2, 3].includes(rank) && (
                <StarIcon
                  height={24}
                  width={24}
                />
              )}
              {rank > 3 && (
                <ArrowUpIcon
                  height={24}
                  width={24}
                />
              )}
            </div>

            <div className="flex flex-col">
              <Typography
                color="text.secondary"
                variant="body4"
              >
                {leaderboard.length > 0 && nextPlace !== null && (
                  <FormattedMessage
                    id="promo.rakeRace.table.userRank"
                    values={{
                      nextPlace,
                      diffToNextPlace: usd(nextPlaceRake - value),
                    }}
                  />
                )}
                {rank === 1 && <FormattedMessage id="promo.rakeRace.table.firstPlace" />}
              </Typography>
              <Typography
                color="text.primary"
                variant="body2"
              >
                {readout}
              </Typography>
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'prize',
      header: formatMessage({ id: 'promo.rakeRace.table.prize' }),
      ...fluidColumn({ min: 120 }),
      cell: ({ row }) => prizeForRank(row.original.rank)?.title ?? '-',
    },
    {
      accessorKey: 'prizeValue',
      header: formatMessage({ id: 'promo.rakeRace.table.prizeValue' }),
      ...fluidColumn({ min: 120 }),
      cell: ({ row }) => {
        const sum = safeNumber(prizeForRank(row.original.rank)?.prize ?? '')

        return sum ? usd(sum, 0) : '-'
      },
    },
  ])
}
